import { apiUrl } from "./config";
import { beginHttpTiming } from "./performanceDiagnostics";

/**
 * Erro padronizado de chamadas HTTP a API.
 * - `status` e o HTTP status (0 quando rede falhou antes da resposta).
 * - `body` e o payload do erro, se houver (ja parseado quando possivel).
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;

  constructor(message: string, status: number, body: unknown = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

/**
 * Sessao perdida no meio do uso.
 *
 * Um cookie expira, ou um admin revoga o acesso, e a proxima leitura volta 401.
 * Sem um ponto central, cada tela trataria isso sozinha — e o painel ficaria
 * mostrando "erro ao carregar" em oito lugares em vez de dizer que a sessao
 * caiu. A camada HTTP e o unico lugar por onde tudo passa, entao o aviso nasce
 * aqui e o `AuthProvider` escuta.
 *
 * `/api/auth/*` fica de fora: um 401 ali e a resposta normal de "senha errada"
 * ou "ainda nao logado", nao uma sessao que caiu.
 */
type OuvinteSessao = () => void;
const ouvintesSessao = new Set<OuvinteSessao>();
let sessionGeneration = 0;

/** Identifica a sessão local para filas assíncronas; não é token/credencial. */
export function getSessionGeneration(): number { return sessionGeneration; }

export function aoPerderSessao(ouvinte: OuvinteSessao): () => void {
  ouvintesSessao.add(ouvinte);
  return () => ouvintesSessao.delete(ouvinte);
}

function notificarSessaoPerdida(path: string): void {
  if (path.startsWith("/api/auth/")) return;
  for (const ouvinte of ouvintesSessao) ouvinte();
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

async function parseBody(res: Response): Promise<unknown> {
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) {
    try {
      return await res.json();
    } catch (error) {
      if ((error as { name?: string })?.name === "AbortError") throw error;
      throw new ApiError("Resposta JSON incompleta ou inválida. Tente novamente.", res.status);
    }
  }
  try {
    const text = await res.text();
    return text.length > 0 ? text : null;
  } catch (error) {
    if ((error as { name?: string })?.name === "AbortError") throw error;
    throw new ApiError("Não foi possível ler a resposta. Tente novamente.", res.status);
  }
}

/**
 * Faz uma chamada HTTP a API e devolve o corpo ja parseado.
 *
 * - Em status 2xx: resolve com o JSON tipado como T.
 * - Em status de erro: lanca ApiError (com status e body).
 * - Em falha de rede / abort: lanca ApiError com status=0 (ou propaga AbortError).
 */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const timing = beginHttpTiming(path, opts.method);
  const generation = sessionGeneration;
  try {
    const result = await performRequest<T>(path, opts, timing, generation);
    timing?.finish("complete");
    return result;
  } catch (error) {
    timing?.finish((error as { name?: string })?.name === "AbortError" ? "aborted" : "error");
    throw error;
  }
}

async function performRequest<T>(path: string, opts: RequestOptions, timing: ReturnType<typeof beginHttpTiming>, generation: number): Promise<T> {
  const { method = "GET", body, signal, headers } = opts;

  const init: RequestInit = {
    method,
    signal,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : null),
      ...headers,
    },
  };

  if (body !== undefined) {
    init.body = typeof body === "string" ? body : JSON.stringify(body);
  }

  let res: Response;
  try {
    res = await fetch(apiUrl(path), init);
    timing?.headers(res);
  } catch (err) {
    if ((err as { name?: string }).name === "AbortError") throw err;
    const message = err instanceof Error ? err.message : "Falha de rede";
    throw new ApiError(`Falha ao chamar ${method} ${path}: ${message}`, 0, null);
  }

  // Uma resposta antiga, inclusive 401 de PUT sem AbortSignal, pertence à
  // identidade que iniciou o pedido: não pode derrubar nem alimentar a nova.
  if (generation !== sessionGeneration || signal?.aborted) throw abortError();
  if (!res.ok) {
    if (res.status === 401) notificarSessaoPerdida(path);
    const parsed = await parseBody(res);
    const detail =
      parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : parsed && typeof parsed === "object" && "message" in parsed
          ? String((parsed as { message: unknown }).message)
          : res.statusText;
    throw new ApiError(`HTTP ${res.status} em ${method} ${path}: ${detail}`, res.status, parsed);
  }

  if (res.status === 204) return undefined as T;
  const parsed = (await parseBody(res)) as T;
  // O corpo pode terminar de chegar depois do logout, mesmo com headers 200.
  if (generation !== sessionGeneration || signal?.aborted) throw abortError();
  return parsed;
}

export const http = {
  get: <T>(path: string, opts?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...opts, method: "GET" }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...opts, method: "POST", body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...opts, method: "PUT", body }),
};

// Coalescência de GETs em voo, keyed pela URL (path). Várias chamadas
// concorrentes à MESMA URL compartilham UMA requisição de rede — mata a
// rajada de mount (Sidebar + dashboard, com StrictMode dobrando efeitos em
// dev, pedindo o mesmo endpoint no mesmo tick). A entrada some assim que a
// requisição assenta: NÃO é cache temporal, então chamadas espaçadas (ex.:
// poll de 5s) ainda refazem a busca e a frescura do dado não muda.
interface SharedGet {
  promise: Promise<unknown>;
  controller: AbortController;
  consumers: number;
}
const inFlightGets = new Map<string, SharedGet>();
export const GET_TIMEOUT_MS = 30_000;

/** Fronteira de identidade da aba, inclusive novo login da mesma pessoa.
 * Abort é economia de rede; a época também rejeita respostas já em trânsito.
 * Não desfaz mutações que o servidor possa ter concluído. */
export function invalidateSessionRequests(): void {
  sessionGeneration += 1;
  const previous = [...inFlightGets.values()];
  inFlightGets.clear();
  for (const entry of previous) entry.controller.abort();
}

/** A proxima leitura depois de uma mutacao nao pode aderir a um GET anterior. */
export function invalidateCoalescedGets(matches: (path: string) => boolean): void {
  for (const path of inFlightGets.keys()) {
    if (matches(path)) inFlightGets.delete(path);
  }
}

function abortError(): DOMException {
  return new DOMException("Aborted", "AbortError");
}

/**
 * GET com coalescência de requisições em voo.
 *
 * - Chamadas concorrentes ao mesmo `path` recebem a MESMA promise/rede.
 * - O `signal` controla apenas a espera DAQUELE chamador: abortá-lo rejeita
 *   só a promise dele (AbortError), sem cancelar a requisição compartilhada
 *   que outros chamadores ainda aguardam.
 * - Sem consumidores, cancela tambem a rede e libera a URL imediatamente.
 * - Timeout de 30s inclui leitura do corpo e aparece como erro recuperavel.
 * - Só para leituras idempotentes. Mutações (POST/PUT) nunca passam por aqui.
 *
 * A resposta resolvida é compartilhada por referência entre chamadores
 * concorrentes — trate-a como imutável (a camada já é funcional).
 */
export function coalescedGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  if (signal?.aborted) return Promise.reject(abortError());
  let shared = inFlightGets.get(path);
  if (!shared) {
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, GET_TIMEOUT_MS);
    const entry: SharedGet = { controller, consumers: 0, promise: Promise.resolve() };
    entry.promise = request<T>(path, { signal: controller.signal })
      .catch((error: unknown) => {
        if (timedOut) throw new ApiError("A leitura demorou demais. Tente novamente.", 0);
        throw error;
      })
      .finally(() => {
        clearTimeout(timer);
        // Uma resposta cancelada nao pode apagar a chamada nova da mesma URL.
        if (inFlightGets.get(path) === entry) inFlightGets.delete(path);
      });
    shared = entry;
    inFlightGets.set(path, shared);
  }

  const entry = shared;
  entry.consumers += 1;
  return new Promise<T>((resolve, reject) => {
    let finished = false;
    const release = () => {
      if (finished) return;
      finished = true;
      signal?.removeEventListener("abort", onAbort);
      entry.consumers -= 1;
      if (entry.consumers === 0) {
        if (inFlightGets.get(path) === entry) inFlightGets.delete(path);
        entry.controller.abort();
      }
    };
    const onAbort = () => { release(); reject(abortError()); };
    signal?.addEventListener("abort", onAbort, { once: true });
    entry.promise.then(
      (value) => {
        release();
        resolve(value as T);
      },
      (err) => {
        release();
        reject(err);
      },
    );
  });
}
